import React, { useContext, useEffect, useState } from "react";
import {
  ArrowUp,
  ArrowDown,
  Clock,
  Users,
  AlertCircle,
  CheckCircle2,
  Briefcase,
  Calendar,
  Activity,
  PieChart,
  BarChart,
  Layers,
  Settings,
  Bell,
  DollarSign,
  CalendarCheck,
} from "lucide-react";
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { FaRupeeSign } from "react-icons/fa";
import { AuthContext } from "../../../context/AuthContext";
import BillsManagement from "./BillsManagement"; // Import the new component
import API from "../../../services/api";

const AdminDashboard = () => {
    const [projectStatusData, setProjectStatusData] = useState([]);
    const [statsData, setStatsData] = useState(null);
const [statsLoading, setStatsLoading] = useState(true);


  const [error, setError] = useState(null);

  const { 
    user, 
    getClients,
    getTickets, 
    setTickets, 
    tickets, 
    ticketLoading, 
    ticketError, 
    getAllBills, 
    bills, 
    setBills, 
    billsLoading, 
    billsError ,

    getProjects, projects, setProjects, projectLoading,
    getEmployees, 
  } = useContext(AuthContext);

// Fetch projects on component mount
  useEffect(() => {
    getClients();
    getEmployees();
      fetchStatistics();

  }, []);

// Fetch projects on component mount
  useEffect(() => {
    if (!projects.length)
    getProjects();
  }, [getProjects, projects]);


  const fetchStatistics = async () => {
  try {
    setStatsLoading(true);
    const response = await API.get('/api/statistics');

    console.log("Stats: ", response.data.data)
    if (response.data.success) {
      setStatsData(response.data.data);
    }
  } catch (error) {
    console.error('Error fetching statistics:', error);
  } finally {
    setStatsLoading(false);
  }
};


   // Calculate paid amount for a bill
  const calculatePaidAmount = (bill) => {
    if (!bill.paidAmount || bill.paidAmount.length === 0) return 0;
    return bill.paidAmount.reduce((sum, payment) => sum + parseFloat(payment.amount), 0);
  };

  //Find Total Revenue
  const totalRevenue = bills && bills.length > 0 ?
    bills
      .reduce((total, item) => {
        const paidAmount = item.paidAmount
          ? item.paidAmount.reduce(
            (sum, payment) => sum + parseFloat(payment.amount),
            0
          )
          : 0;
        return total + paidAmount;
      }, 0)
      .toLocaleString() +
    " " +
    (bills[0]?.currency || "INR") : "0 INR";

  // Stats using API data with fallbacks
  const stats = statsData ? [
    {
      title: "Active Projects",
      value: statsData.projects?.active || 0,
      icon: <Briefcase size={20} />,
      change: `${statsData.projects?.monthlyChange >= 0 ? '+' : ''}${statsData.projects?.monthlyChange || 0}`,
      isPositive: (statsData.projects?.monthlyChange || 0) >= 0,
      color: "text-blue-600 bg-blue-100",
    },
    {
      title: "Total Clients",
      value: statsData.clients?.total || 0,
      icon: <Users size={20} />,
      change: `${statsData.clients?.monthlyChange >= 0 ? '+' : ''}${statsData.clients?.monthlyChange || 0}%`,
      isPositive: (statsData.clients?.monthlyChange || 0) >= 0,
      color: "text-green-600 bg-green-100",
    },
    {
      title: "Open Tickets",
      value: statsData.tickets?.open || 0,
      icon: <AlertCircle size={20} />,
      change: `${statsData.tickets?.monthlyChange >= 0 ? '+' : ''}${statsData.tickets?.monthlyChange || 0}`,
      isPositive: (statsData.tickets?.monthlyChange || 0) <= 0, // For tickets, decrease is positive
      color: "text-purple-600 bg-purple-100",
    },
    {
      title: "Revenue",
      value: statsData.revenue?.total ? 
        `${statsData.revenue.total.toLocaleString()} ${statsData.revenue.currency || 'INR'}` : 
        "0 INR",
      icon: <FaRupeeSign size={20} />,
      change: `${statsData.revenue?.monthlyChange >= 0 ? '+' : ''}${statsData.revenue?.monthlyChange || 0}%`,
      isPositive: (statsData.revenue?.monthlyChange || 0) >= 0,
      color: "text-amber-600 bg-amber-100",
    },
  ] : [
    {
      title: "Active Projects",
      value: 0,
      icon: <Briefcase size={20} />,
      change: "+0",
      isPositive: true,
      color: "text-blue-600 bg-blue-100",
    },
    {
      title: "Total Clients",
      value: 0,
      icon: <Users size={20} />,
      change: "+0%",
      isPositive: true,
      color: "text-green-600 bg-green-100",
    },
    {
      title: "Open Tickets",
      value: 0,
      icon: <AlertCircle size={20} />,
      change: "+0",
      isPositive: true,
      color: "text-purple-600 bg-purple-100",
    },
    {
      title: "Revenue",
      value: "0 INR",
      icon: <FaRupeeSign size={20} />,
      change: "+0%",
      isPositive: true,
      color: "text-amber-600 bg-amber-100",
    },
  ];

  useEffect(() => {
    if (projects.length === 0) {
      setProjectStatusData([
        { name: "Active", value: 0, color: "#3b82f6" },
        { name: "Planning", value: 0, color: "#8b5cf6" },
        { name: "Paused", value: 0, color: "#f59e0b" },
        { name: "Completed", value: 0, color: "#10b981" },
        { name: "Cancelled", value: 0, color: "#ef4444" },
      ]);
      return;
    }

    const statusCounts = projects.reduce((acc, project) => {
      acc[project.status] = (acc[project.status] || 0) + 1;
      return acc;
    }, {});

    const calculatedData = [
      { name: "Active", value: statusCounts.ACTIVE || 0, color: "#3b82f6" },
      { name: "Planning", value: statusCounts.PLANNING || 0, color: "#8b5cf6" },
      { name: "Paused", value: statusCounts.PAUSED || 0, color: "#f59e0b" },
      { name: "Completed", value: statusCounts.COMPLETED || 0, color: "#10b981" },
      { name: "Cancelled", value: statusCounts.CANCELLED || 0, color: "#ef4444" },
    ];

    setProjectStatusData(calculatedData);
  }, [projects]);

  // Pie chart data
  // const projectStatusData = [
  //   { name: "Active", value: 14, color: "#3b82f6" },
  //   { name: "Planning", value: 4, color: "#8b5cf6" },
  //   { name: "Paused", value: 2, color: "#f59e0b" },
  //   { name: "Completed", value: 8, color: "#10b981" },
  //   { name: "Cancelled", value: 1, color: "#ef4444" },
  // ];

  // Bar chart data
  const ticketsByDepartmentData = [
    { name: "Development", open: 18, inProgress: 12, resolved: 24 },
    { name: "Design", open: 8, inProgress: 6, resolved: 15 },
    { name: "QA", open: 14, inProgress: 9, resolved: 11 },
    { name: "DevOps", open: 7, inProgress: 4, resolved: 9 },
  ];

  const topEmployees = [
    {
      name: "Sarah Johnson",
      position: "Senior Developer",
      tasksCompleted: 38,
      photo: "/api/placeholder/40/40",
      rating: 4.9,
    },
    {
      name: "Michael Chen",
      position: "Backend Engineer",
      tasksCompleted: 32,
      photo: "/api/placeholder/40/40",
      rating: 4.7,
    },
    {
      name: "Emily Wilson",
      position: "UX Designer",
      tasksCompleted: 29,
      photo: "/api/placeholder/40/40",
      rating: 4.8,
    },
    {
      name: "David Smith",
      position: "Frontend Developer",
      tasksCompleted: 27,
      photo: "/api/placeholder/40/40",
      rating: 4.6,
    },
  ];

  const systemStatus = [
    {
      service: "API Services",
      status: "Operational",
      uptime: "99.99%",
      color: "text-green-600",
    },
    {
      service: "Database Cluster",
      status: "Operational",
      uptime: "99.95%",
      color: "text-green-600",
    },
    {
      service: "Authentication Service",
      status: "Operational",
      uptime: "99.98%",
      color: "text-green-600",
    },
    {
      service: "Storage Services",
      status: "Degraded",
      uptime: "98.75%",
      color: "text-amber-600",
    },
  ];

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "LOW":
        return "bg-gray-100 text-gray-800";
      case "MEDIUM":
        return "bg-blue-100 text-blue-800";
      case "HIGH":
        return "bg-orange-100 text-orange-800";
      case "URGENT":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "OPEN":
        return "bg-blue-100 text-blue-800";
      case "IN_PROGRESS":
        return "bg-yellow-100 text-yellow-800";
      case "PENDING":
        return "bg-purple-100 text-purple-800";
      case "RESOLVED":
        return "bg-green-100 text-green-800";
      case "CLOSED":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getBillStatusColor = (status) => {
    switch (status) {
      case "PAID":
        return "bg-green-100 text-green-800";
      case "PENDING":
        return "bg-yellow-100 text-yellow-800";
      case "OVERDUE":
        return "bg-red-100 text-red-800";
      case "CANCELLED":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

 

  // Calculate outstanding amount for a bill
  const calculateOutstanding = (bill) => {
    const totalAmount = parseFloat(bill.amount);
    const paidAmount = calculatePaidAmount(bill);
    return totalAmount - paidAmount;
  };


  //Fetch All Ticket
  useEffect(() => {
    if (!tickets.length) {
      getTickets();
    }
  }, [tickets, getTickets]);

  //Fetch All Bills
  useEffect(() => {
    if (!bills.length) {
      getAllBills();
    }
  }, [bills, getAllBills]);

  const renderActiveTabContent = () => {
    
        return (
          <div className="space-y-8">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {statsLoading ? (
                // Loading state for stats
                Array.from({ length: 4 }).map((_, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-lg shadow p-6 animate-pulse"
                  >
                    <div className="flex items-center justify-between">
                      <div className="space-y-2">
                        <div className="h-4 bg-gray-200 rounded w-24"></div>
                        <div className="h-8 bg-gray-200 rounded w-16"></div>
                        <div className="h-3 bg-gray-200 rounded w-20"></div>
                      </div>
                      <div className="h-12 w-12 bg-gray-200 rounded-full"></div>
                    </div>
                  </div>
                ))
              ) : (
                stats.map((stat, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-lg shadow p-6 flex items-center justify-between transition-all hover:shadow-md"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-600">
                        {stat.title}
                      </p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">
                        {stat.value}
                      </p>
                      <div
                        className={`flex items-center mt-1 ${stat.isPositive ? "text-green-600" : "text-red-600"
                          }`}
                      >
                        {stat.isPositive ? (
                          <ArrowUp size={16} />
                        ) : (
                          <ArrowDown size={16} />
                        )}
                        <span className="text-xs font-medium ml-1">
                          {stat.change} from last month
                        </span>
                      </div>
                    </div>
                    <div className={`rounded-full p-3 ${stat.color}`}>
                      {stat.icon}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Payment Summary with API Data */}
            <div className="bg-gray-50 px-4 rounded-md">
              <h2 className="text-lg font-semibold text-gray-700 mb-4">
                Payment Summary
              </h2>
              {statsLoading ? (
                <div className="flex items-center justify-center p-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-600">Loading payment data...</span>
                </div>
              ) : statsData ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white p-4 rounded-md shadow-sm">
                    <p className="text-sm font-medium text-gray-500">
                      Total Revenue
                    </p>
                    <p className="text-2xl font-bold text-green-600">
                      {statsData.revenue?.total?.toLocaleString() || '0'}{' '}
                      {statsData.revenue?.currency || 'INR'}
                    </p>
                  </div>
                  <div className="bg-white p-4 rounded-md shadow-sm">
                    <p className="text-sm font-medium text-gray-500">
                      Pending Amount
                    </p>
                    <p className="text-2xl font-bold text-yellow-600">
                      {statsData.revenue?.pending?.toLocaleString() || '0'}{' '}
                      {statsData.revenue?.currency || 'INR'}
                    </p>
                  </div>
                  <div className="bg-white p-4 rounded-md shadow-sm">
                    <p className="text-sm font-medium text-gray-500">
                      Overdue Amount
                    </p>
                    <p className="text-2xl font-bold text-red-600">
                      {statsData.revenue?.overdue?.toLocaleString() || '0'}{' '}
                      {statsData.revenue?.currency || 'INR'}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="p-6 text-center text-gray-500">
                  Unable to load payment data
                </div>
              )}
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Pie Chart */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Project Status Distribution
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                      <Pie
                        data={projectStatusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {projectStatusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => [`${value} Projects`, null]}
                      />
                      <Legend />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Bar Chart */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Tickets by Department
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsBarChart
                      data={ticketsByDepartmentData}
                      barSize={20}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="open" name="Open" fill="#3b82f6" />
                      <Bar
                        dataKey="inProgress"
                        name="In Progress"
                        fill="#f59e0b"
                      />
                      <Bar dataKey="resolved" name="Resolved" fill="#10b981" />
                    </RechartsBarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            

            {/* Tickets Table */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-800">
                  Recent Tickets
                </h2>
                <a
                  href="/dashboard/ticket"
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  View All
                </a>
              </div>

              <div className="overflow-x-auto">
                {ticketLoading ? (
                  <div className="flex items-center justify-center p-6">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-3 text-gray-600">
                      Loading tickets...
                    </span>
                  </div>
                ) : error ? (
                  <div className="p-6 text-center text-red-500">{error}</div>
                ) : tickets.length === 0 ? (
                  <div className="p-6 text-center text-gray-500">
                    No tickets found
                  </div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Ticket ID
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Title
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Priority
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Status
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Date
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {tickets.slice(0, 5).map((ticket) => (
                        <tr key={ticket._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {ticket.kkgtTicketId ||
                              ticket._id.slice(-6).toUpperCase()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {ticket.title}
                            </div>
                            <div className="text-sm text-gray-500">
                              {ticket.project?.name || "No Project"}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getPriorityColor(
                                ticket.priority
                              )}`}
                            >
                              {ticket.priority}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(
                                ticket.status
                              )}`}
                            >
                              {ticket.status.replace("_", " ")}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(ticket.updatedAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Bills Management Component */}
            <BillsManagement />

          </div>
        );
      
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Main Content */}
      <div className="flex-1">
        {/* Header */}
        <header className="bg-white shadow-sm">
          <div className=" mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16 items-center">
              <h2 className="text-lg font-semibold text-gray-900">
              </h2>
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <button className="relative p-1 text-gray-400 hover:text-gray-500 focus:outline-none">
                    <Bell size={20} />
                    <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-red-500"></span>
                  </button>
                </div>
                <div className="flex items-center space-x-2">
                  <img
                    className="h-8 w-8 rounded-full"
                    src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcT87nae4416RrOQAPy60JLL9bVfrTkAal_K7Q&s"
                    alt="User"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Admin User
                  </span>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="mx-auto py-6">{renderActiveTabContent()}</main>
      </div>
    </div>
  );
};

export default AdminDashboard;